import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {CommonModule, DOCUMENT, isPlatformBrowser} from '@angular/common';
import {UmpireComponentSocialMedia} from '@tisa-group/models';

@Component({
  selector: 'tisa-social-media-post',
  imports: [CommonModule],
  templateUrl: './social-media-post.component.html',
  styleUrl: './social-media-post.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SocialMediaPostComponent {
  @Input() component: UmpireComponentSocialMedia | undefined;

  @ViewChild('embedWrapper', {read: ElementRef}) readonly embedWrapper:
    | ElementRef
    | undefined;

  private readonly loadedScripts = new Set<string>();

  constructor(
    private readonly renderer: Renderer2,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    @Inject(DOCUMENT)
    private readonly document: Document & {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      defaultView: Document['defaultView'] & {FB: any; twttr: any};
    },
  ) {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }
      const wrapperElement = this.embedWrapper?.nativeElement;

      if (wrapperElement && this.component) {
        switch (this.component.social_media_type) {
          case 'facebook':
            this.handleFacebookPost(wrapperElement);
            break;
          case 'twitter':
            this.handleTweet(wrapperElement);
            break;
          case 'tiktok':
            this.handleTikTokPost(wrapperElement);
            break;
        }
      }
    });
  }

  private createTweet(wrapperElement: HTMLElement): void {
    if (!this.component?.external_url) {
      return;
    }

    const tweetId = new URL(this.component.external_url).pathname.split('/').pop();

    wrapperElement.innerHTML = '';
    this.document.defaultView?.twttr?.widgets?.createTweet(tweetId, wrapperElement);
  }

  private handleTweet(wrapperElement: HTMLElement): void {
    if (this.document.defaultView?.twttr?.widgets) {
      this.createTweet(wrapperElement);
    } else {
      this.initializeSocialSDK('https://platform.twitter.com/widgets.js', () => {
        this.createTweet(wrapperElement);
      });
    }
  }

  private createFacebookPost(wrapperElement: HTMLElement): void {
    if (!this.component?.external_url) {
      return;
    }

    wrapperElement.innerHTML = '';
    wrapperElement.classList.add('fb-post');
    wrapperElement.setAttribute('data-href', this.component.external_url);

    const FB = this.document.defaultView?.FB;
    if (FB) {
      FB.init({
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v17.0',
      });

      FB.XFBML.parse(wrapperElement);
    }
  }

  private handleFacebookPost(wrapperElement: HTMLElement): void {
    if (this.document.defaultView?.FB) {
      this.createFacebookPost(wrapperElement);
    } else {
      this.initializeSocialSDK(
        'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v17.0',
        () => {
          this.createFacebookPost(wrapperElement);
        },
      );
    }
  }

  private handleTikTokPost(wrapperElement: HTMLElement): void {
    if (!this.component?.external_url) {
      return;
    }

    const videoIdMatch = this.component.external_url.match(/\/video\/(\d+)/);
    if (!videoIdMatch) {
      console.error(
        'Could not extract TikTok video ID from URL:',
        this.component.external_url,
      );
      return;
    }

    const videoId = videoIdMatch[1];
    const iframe = this.renderer.createElement('iframe');

    iframe.src = `https://www.tiktok.com/player/v1/${videoId}`;
    iframe.height = '718';
    iframe.width = '552';
    iframe.frameBorder = '0';
    iframe.allow = 'encrypted-media';
    iframe.style.border = 'none';

    wrapperElement.innerHTML = '';
    this.renderer.appendChild(wrapperElement, iframe);
  }

  private initializeSocialSDK(src: string, onload?: () => void): void {
    if (this.loadedScripts.has(src)) {
      onload?.();
      return;
    }

    const script = this.renderer.createElement('script') as HTMLScriptElement;
    script.type = 'text/javascript';
    script.src = src;
    script.async = true;

    if (onload) {
      script.onload = () => {
        this.loadedScripts.add(src);
        onload();
      };
    } else {
      script.onload = () => this.loadedScripts.add(src);
    }

    script.onerror = () => {
      console.error(`Failed to load script: ${src}`);
    };

    this.renderer.appendChild(this.document.head, script);
  }
}
